import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { RequireAdmin, RequireFamily, SignedIn } from "@/auth/guards";
import { ForgotPasswordPage } from "@/auth/screens/ForgotPasswordPage";
import { SignInPage } from "@/auth/screens/SignInPage";
import { SignUpPage } from "@/auth/screens/SignUpPage";
import { VerifyEmailPage } from "@/auth/screens/VerifyEmailPage";
import { Onboarding } from "@/onboarding/Onboarding";
import { siteRoutes } from "@/site/routes";
import { AchievementsPage } from "./pages/achievements/AchievementsPage";
import { ActionsPage } from "./pages/actions/ActionsPage";
import { AdminPage } from "./pages/admin/AdminPage";
import { AssetsPage } from "./pages/assets/AssetsPage";
import { BudgetPage } from "./pages/budget/BudgetPage";
import { CashFlowPage } from "./pages/cashflow/CashFlowPage";
import { DebtPage } from "./pages/debt/DebtPage";
import { DecisionsPage } from "./pages/decisions/DecisionsPage";
import { HomePage } from "./pages/home/HomePage";
import { NetWorthPage } from "./pages/networth/NetWorthPage";
import { PrioritiesPage } from "./pages/priorities/PrioritiesPage";
import { RetirementPage } from "./pages/retirement/RetirementPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { SharpenPage } from "./pages/sharpen/SharpenPage";
import { UpdateDataPage } from "./pages/updatedata/UpdateDataPage";
import { AppNotFound, RouteError } from "./RouteError";
import { RootLayout } from "./RootLayout";
import { AppShell } from "./shell/AppShell";

/**
 * Route map. The site agent's routes (src/site/routes.tsx) mount at the root next to these.
 *
 * - /signin, /signup, /forgot-password, /verify-email: auth screens
 * - /start: onboarding (anonymous until its Account step)
 * - /app/*: the signed-in app (needs a saved household, otherwise to /start)
 * - /app/decisions/:tool: the Decisions tools (slugs from plan-logic/decisions/tools.ts)
 * - /admin: admins only (/admin/blog is the site's)
 */
export const appRoutes: RouteObject[] = [
  { path: "signin", element: <SignInPage /> },
  { path: "signup", element: <SignUpPage /> },
  { path: "forgot-password", element: <ForgotPasswordPage /> },
  { path: "verify-email", element: <VerifyEmailPage /> },
  { path: "start/*", element: <Onboarding /> },
  {
    path: "app",
    element: (
      <SignedIn>
        <RequireFamily>
          <AppShell />
        </RequireFamily>
      </SignedIn>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: "actions", element: <ActionsPage /> },
      { path: "cash-flow", element: <CashFlowPage /> },
      { path: "budget", element: <BudgetPage /> },
      { path: "assets", element: <AssetsPage /> },
      { path: "net-worth", element: <NetWorthPage /> },
      { path: "priorities", element: <PrioritiesPage /> },
      { path: "priorities/edit", element: <PrioritiesPage edit /> },
      { path: "retirement", element: <RetirementPage /> },
      { path: "debt", element: <DebtPage /> },
      { path: "decisions", element: <DecisionsPage /> },
      { path: "decisions/:tool", element: <DecisionsPage /> },
      { path: "update-data", element: <UpdateDataPage /> },
      { path: "achievements", element: <AchievementsPage /> },
      // Runs Short became the Actions page (prototype v42); old links land there.
      { path: "runs-short", element: <Navigate to="/app/actions" replace /> },
      { path: "sharpen", element: <SharpenPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "*", element: <AppNotFound /> },
    ],
  },
  {
    path: "admin",
    element: (
      <SignedIn>
        <RequireAdmin>
          <AdminPage />
        </RequireAdmin>
      </SignedIn>
    ),
  },
];

/** Dev server only: the page gallery against test fixtures (src/dev/Gallery.tsx). */
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? (["app", "start"] as const).map((area) => ({
      path: `dev/${area}/*`,
      lazy: async () => {
        const { Gallery } = await import("@/dev/Gallery");
        return { Component: () => <Gallery area={area} /> };
      },
    }))
  : [];

export const routes: RouteObject[] = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        // A second boundary inside the providers, so a page error keeps auth and the query cache.
        errorElement: <RouteError />,
        // The site owns the root catch-all (its own 404); /app/* has the app's.
        children: [...siteRoutes, ...appRoutes, ...devRoutes],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
